/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */

import { toArray, isFunction, isNil } from 'lodash';



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */
const
	propertyKey = {
		providers: Symbol('providers')
	};



/* ========================================================================== *\
	TYPE DEFINITIONS
\* ========================================================================== */
/**
 * @callback MetadataProvider
 *
 * @param {any} data
 * @returns {Promise}
*/



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */
/**
 * Wraps a promise in another promise which will always be fulfilled no matter
 * the result of the original promise.
 * When the original promise is fulfilled the result of original is returned.
 * In case the original promise failed, the promise will resolve with null.
 *
 * @param {Function} provider
 * @param {any} data
 *
 * @returns {Promise<any>}
 */
function safeCallProvider(provider, data) {
	let providerResult;
	// Perform the provider call.
	try {
		providerResult = provider(data);
	} catch (exception) {
		providerResult = null;
	}

	// Check if the result of the provider is something that resembles a
	// Promise (it is not nil and it should have a method named "then"). When it
	// fails these checks, just return the provider result.
	if (
		isNil(providerResult) ||
		!providerResult.then
	) {
		return providerResult;
	}

	// Wrap the provider promise in another promise so we can always fulfill the
	// result, preventing the Promise.all to exit early.
	return new Promise(resolve => {
		providerResult
			.then(result => resolve(result))
			.catch(() => resolve(null));
	});
}



/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */
class MetadataProviderManager {
	/* ====================================================================== *\
		CONSTRUCTOR
	\* ====================================================================== */
	constructor() {
		this[propertyKey.providers] = new Set();
	}



	/* ====================================================================== *\
		PUBLIC API
	\* ====================================================================== */
	applyMetadataProviders(data) {
		// Call the provider in such a manner that providers which return a
		// promise will always be resolved and not rejected. This will prevent
		// the Promise.all to return early when one of the promises fail.
		const
			promises = toArray(this[propertyKey.providers]).map(provider => safeCallProvider(provider, data));

		return Promise.all(promises);
	}

	/**
	 * Adds a metadata provider to the manager.
	 *
	 * @param {MetadataProvider} provider The metadata provider to add.
	 */
	registerProvider(provider) {
		if (
			!isFunction(provider) ||
			this[propertyKey.providers].has(provider)
		) {
			return false;
		}

		this[propertyKey.providers].add(provider);

		return true;
	}

	/**
	 * Removes a metadata provider from the manager.
	 *
	 * @param {MetadataProvider} provider The metadata provider to remove.
	 */
	unregisterProvider(provider) {
		if (
			!isFunction(provider) ||
			!this[propertyKey.providers].has(provider)
		) {
			return false;
		}

		(this[propertyKey.providers]).delete(provider);

		return true;
	}
}



/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */
export default MetadataProviderManager;
